/**
 * Client checks for the Phase 19 F19 readiness records. The server applies the same rules
 * (clinicalReadiness.js) and answers 400 INVALID_SOC_ITEM_FIELD with details.field.
 */
import type {
  AdvanceDirectiveItemInput,
  EmergencyPlanItemInput,
  ServicesItemInput,
  ThstepsItemInput,
} from '../../../../dtos/patientmr/socItems.dto';
import { OTHER_SERVICE_TYPES, TRIAGE_CATEGORIES } from '../../../../dtos/patientmr/socItems.dto';
import { ApiError } from '../../../../dtos/common/api.dto';

export type FieldErrors = Record<string, string>;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
export const isIsoDate = (v: string | null | undefined): v is string => !!v && ISO_DATE.test(v) && !Number.isNaN(Date.parse(v));

/** Age in whole years on `today`, or null without a usable date of birth */
export const ageYears = (dob: string | undefined | null, today = new Date()): number | null => {
  const t = Date.parse(dob || '');
  if (Number.isNaN(t)) return null;
  return (today.getTime() - t) / (365.25 * 86400000);
};

export const isMinor = (dob: string | undefined | null, today = new Date()): boolean => {
  const age = ageYears(dob, today);
  return age !== null && age < 18;
};

export function validateAdvanceDirective(input: AdvanceDirectiveItemInput, patientDob?: string | null, today = new Date()): FieldErrors {
  const errors: FieldErrors = {};
  if (input.adFormDate && !isIsoDate(input.adFormDate)) errors.adFormDate = 'Enter the form date as a valid date.';
  if (input.adStatus === 'OOH_DNR') {
    if (!isIsoDate(input.adFormDate)) errors.adFormDate = 'The date of the OOH-DNR form is required.';
    if (isMinor(patientDob, today) && input.adTerminalDxAttested !== true) {
      errors.adTerminalDxAttested = 'For a patient under 18, an OOH-DNR requires a physician diagnosis of a terminal or irreversible condition (HSC §166.085). Confirm the attestation.';
    }
  }
  return errors;
}

export function validateEmergencyPlan(input: EmergencyPlanItemInput): FieldErrors {
  const errors: FieldErrors = {};
  if (input.emergencyTriageCategory && !TRIAGE_CATEGORIES.includes(input.emergencyTriageCategory)) {
    errors.emergencyTriageCategory = 'Choose a triage category from 1 to 4.';
  }
  return errors;
}

export function validateThsteps(input: ThstepsItemInput, today = new Date()): FieldErrors {
  const errors: FieldErrors = {};
  if (input.thstepsLastCheckup) {
    if (!isIsoDate(input.thstepsLastCheckup)) errors.thstepsLastCheckup = 'Enter the checkup date as a valid date.';
    else if (Date.parse(input.thstepsLastCheckup) > today.getTime()) errors.thstepsLastCheckup = 'The last checkup cannot be in the future.';
  }
  return errors;
}

export function validateServices(input: ServicesItemInput): FieldErrors {
  const errors: FieldErrors = {};
  (input.otherServices || []).forEach((s, i) => {
    if (!OTHER_SERVICE_TYPES.includes(s.type)) errors[`otherServices[${i}].type`] = 'Choose the service type.';
    if (s.hoursPerWeek !== null && (!Number.isFinite(s.hoursPerWeek) || s.hoursPerWeek < 0 || s.hoursPerWeek > 168)) {
      errors[`otherServices[${i}].hoursPerWeek`] = 'Hours per week must be 0 to 168.';
    }
  });
  if ((input.otherServices || []).length > 20) errors.otherServices = 'List at most 20 services.';
  if (input.ispDate && !isIsoDate(input.ispDate)) errors.ispDate = 'Enter the ISP date as a valid date.';
  return errors;
}

/** Map a save error to a field error (INVALID_SOC_ITEM_FIELD) or a general message under `_form` */
export function saveErrorToFields(err: unknown): FieldErrors {
  const message = err instanceof Error && err.message ? err.message : 'The record was not saved. Try again.';
  if (err instanceof ApiError && err.domainCode === 'INVALID_SOC_ITEM_FIELD') {
    const field = typeof err.details?.field === 'string' ? err.details.field : null;
    if (field) return { [field]: message };
  }
  return { _form: message };
}
